import { Component, computed, effect, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { of } from 'rxjs';
import { MceSaveCancelComponent } from '../../components/mce-save-cancel/mce-save-cancel.component';
import { MceSaveCancelConfig, MceSaveCancelEvent } from '../../components/mce-save-cancel/mce-save-cancel.component';
import { form, FormField, maxLength, minLength, required, schema } from '@angular/forms/signals';
import { MceApiService, TokenDto } from '../../../core/mceapi.client.generated';
import { InfoMessageComponent } from '../../components/info-message/info-message.component';

export interface UnregisterUserModel {
  acknowledged: boolean;
  otpToken: string;
  otp: string;
}

const unregisterUserSchema = schema<UnregisterUserModel>((path) => {

  required(path.otpToken);
  minLength(path.otpToken, 6);

  required(path.otp, { message: 'One time password is required' });
  minLength(path.otp, 6, { message: 'One time password must be at least 6 characters' });
  maxLength(path.otp, 6, { message: 'One time password must be 6 characters' });
});


@Component({
  selector: 'mce-unregister-user-dialog',
  imports: [
    FormField,
    MceSaveCancelComponent,
    InfoMessageComponent,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressBarModule,
  ],
  templateUrl: './unregister-user.component.html'
})
export class UnregisterUserComponent {

  readonly dialogRef = inject(MatDialogRef<UnregisterUserComponent>);

  private readonly mceApiService = inject(MceApiService);

  readonly formModel = signal<UnregisterUserModel>({
    acknowledged: false,
    otpToken: '',
    otp: ''
  });
  readonly form = form<UnregisterUserModel>(this.formModel, unregisterUserSchema);

  readonly otpRequest = rxResource<TokenDto | undefined, { requestOtp: boolean }>({
    params: () => ({ requestOtp: this.form.acknowledged().value() && this.form.otpToken().value().length === 0 }),
    stream: ({ params }) => params.requestOtp ? this.mceApiService.v1UsersOtpEmail() : of(undefined),
  });

  _requestOtp = effect(() => {
    const token = this.otpRequest.value()?.token ?? this.form.otpToken().value();
    this.form.otpToken().value.set(token);
  });

  viewState = computed(() => ({
    enableDelete: this.form.acknowledged().value() && this.form().valid(),
    showOtpInput: this.form.otpToken().valid(),
    showOtpError: this.otpRequest.error() !== undefined,
    otpError: this.otpRequest.error(),
    otpSending: this.otpRequest.isLoading(),
  }));

  protected readonly saveCancelConfig = computed<MceSaveCancelConfig>(() => ({
    cancel: { show: true, enable: true }
  }));


  onDeleteAccountClick(): void {
    this.dialogRef.close(this.form().value());
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }

  onSaveCancelAction(event: MceSaveCancelEvent): void {
    if (event.type === 'cancel') {
      this.onCancelClick();
    }
  }
}
